// Reminder efficacy click capture (my-api V240; DESIGN-MY-REMINDER-EFFICACY-
// API-V1 §3). A real reminder email's button URL carries `?rs=<signed tag>`.
// On ANY landing, before routing or the sign-in gate:
//   1. `rs` is stripped from the address bar (history.replaceState — other
//      parameters, the path and the hash are kept), so it never lands in a
//      bookmark, a share or the sign-in returnTo;
//   2. the tag is POSTed once to my-api `POST /reminder-click {rs}` (public:
//      a click counts before sign-in). Fire-and-forget — never awaited, never
//      blocks the page, no UI.
// The tag is KEPT through the sign-in hop: it is stashed (sessionStorage)
// until my-api has answered, so a landing that goes straight to sign-in (or a
// post that did not get through) re-posts it on the next landing in this tab
// — e.g. the returnTo page after sign-in. A tag is posted at most once per tab
// once answered. `rs=test` (operator test sends) is stripped and never posted.
// my-api answers 404 while its MY_REMINDER_EFFICACY_ENABLED is off — harmless
// (and real sends carry no tag then).
// LANDING PRE-FILL: with my-api's MY_REMINDER_LANDING_PREFILL_ENABLED on, an
// assign reminder's click answers 200 {email}; the sign-in screen takes it
// (onReminderLandingEmail) and runs the order-confirmation ?email= lane.

import { myApiBase } from '../data/myApi'

export const RS_PARAM = 'rs'
const PENDING_KEY = 'my.reminderClick.pending'
const POSTED_PREFIX = 'my.reminderClick.posted:'
const RS_SHAPE = /^[A-Za-z0-9_-]{10,120}$/

type Loc = Pick<Location, 'pathname' | 'search' | 'hash'>
type Hist = Pick<History, 'replaceState' | 'state'>
type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

export interface ReminderClickDeps {
  location?: Loc
  history?: Hist
  fetch?: typeof fetch
  storage?: Store | null
  apiBase?: string | null
}

// ---- landing pre-fill hand-off (memory only) --------------------------------
// The address my-api answered for this landing, kept until ONE listener (the
// sign-in screen) takes it. Never written to the URL or to storage.
let landingEmail: string | null = null
const landingListeners = new Set<(email: string) => void>()

function setLandingEmail(email: string): void {
  const [first] = landingListeners
  if (first) {
    landingListeners.delete(first)
    first(email)
    return
  }
  landingEmail = email
}

/** Take the landing address once: `cb` runs now if it already arrived, else
 *  when it does (first listener only). Returns an unsubscribe. */
export function onReminderLandingEmail(cb: (email: string) => void): () => void {
  if (landingEmail !== null) {
    const e = landingEmail
    landingEmail = null
    cb(e)
    return () => {}
  }
  landingListeners.add(cb)
  return () => {
    landingListeners.delete(cb)
  }
}

/** Tests only. */
export function resetReminderLandingForTests(): void {
  landingEmail = null
  landingListeners.clear()
}

function safe<T>(f: () => T, fallback: T): T {
  try {
    return f()
  } catch {
    return fallback
  }
}

/** The landing's path + query + hash without `rs`, or null when it has none. */
export function withoutRs(loc: Loc): { rs: string; url: string } | null {
  const params = new URLSearchParams(loc.search)
  const rs = params.get(RS_PARAM)
  if (rs === null) return null
  params.delete(RS_PARAM)
  const qs = params.toString()
  return { rs, url: `${loc.pathname}${qs ? `?${qs}` : ''}${loc.hash}` }
}

function post(rs: string, d: Required<Omit<ReminderClickDeps, 'storage'>> & { storage: Store | null }): void {
  const base = d.apiBase
  if (!base) return
  safe(() => d.storage?.setItem(PENDING_KEY, rs), undefined)
  void d
    .fetch(`${base.replace(/\/$/, '')}/reminder-click`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ rs }),
      keepalive: true,
    })
    .then((res) => {
      // Answered (recorded, refused or limited): done with this tag. A 5xx
      // keeps it for the next landing.
      if (res.status < 500) {
        safe(() => d.storage?.removeItem(PENDING_KEY), undefined)
        safe(() => d.storage?.setItem(POSTED_PREFIX + rs, '1'), undefined)
      }
      // LANDING PRE-FILL (my-api MY_REMINDER_LANDING_PREFILL_ENABLED): a 200
      // carries the buyer's own address in the BODY. Held in memory only
      // (never the URL, never storage) for the sign-in screen.
      if (res.status === 200) {
        void res
          .json()
          .then((b: unknown) => {
            const e = (b as { email?: unknown } | null)?.email
            if (typeof e === 'string' && /.+@.+\..+/.test(e)) setLandingEmail(e)
          })
          .catch(() => {})
      }
    })
    .catch(() => {
      /* network: kept (pending) for the next landing */
    })
}

/** Run once per page load, before routing. Never throws. */
export function captureReminderClick(deps: ReminderClickDeps = {}): void {
  try {
    const d = {
      location: deps.location ?? window.location,
      history: deps.history ?? window.history,
      fetch: deps.fetch ?? window.fetch.bind(window),
      storage: deps.storage !== undefined ? deps.storage : safe<Store | null>(() => window.sessionStorage, null),
      apiBase: deps.apiBase !== undefined ? deps.apiBase : myApiBase(),
    }
    const landed = withoutRs(d.location)
    if (landed) {
      safe(() => d.history.replaceState(d.history.state, '', landed.url), undefined)
      if (landed.rs !== 'test' && RS_SHAPE.test(landed.rs) && !safe(() => d.storage?.getItem(POSTED_PREFIX + landed.rs), null)) {
        post(landed.rs, d)
        return
      }
    }
    // No (new) tag here: re-post a tag kept from an earlier landing in this
    // tab (the sign-in hop / a post that did not get through).
    const pending = safe(() => d.storage?.getItem(PENDING_KEY) ?? null, null)
    if (pending && RS_SHAPE.test(pending) && !safe(() => d.storage?.getItem(POSTED_PREFIX + pending), null)) post(pending, d)
  } catch {
    /* never block the page */
  }
}
